import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import {
  setFilter,
  setCurrentPageNumber,
  getHomeImageData,
  setReportFilter,
  setReportCurrentPageNumber,
  getReportData,
} from "../../actions";
import { SAITAMA_INSPECTION_FILTER_ITEMS } from "../../config/SaitamaInspectionItems";
import { getIsDeaAdmin } from "../../utils/deaRole";

export const SAITAMA_INSPECTION_ITEMS = SAITAMA_INSPECTION_FILTER_ITEMS;

export const parseSaitamaSort = (sortValue) => {
  const separatorIndex = String(sortValue || "").lastIndexOf("-");
  return {
    type: String(sortValue || "").slice(0, separatorIndex),
    order: String(sortValue || "").slice(separatorIndex + 1),
  };
};

export const buildSaitamaPhotoFilter = ({
  filterStore,
  type,
  order,
  managementId,
  inspectionItems,
  firstCaptureOnly,
  reviewStatus,
}) => ({
  ...filterStore,
  type,
  order,
  managementId,
  inspectionItems,
  firstCaptureOnly,
  reviewStatus,
});

export const resetSaitamaPhotoFilter = (filterStore) => ({
  ...filterStore,
  type: "occurred_datetime",
  order: "desc",
  managementId: "",
  inspectionItems: [],
  firstCaptureOnly: false,
  reviewStatus: "APPROVED",
});

const useStyles = makeStyles((theme) => ({
  container: {
    width: 500,
    height: "100vh",
    padding: 16,
    boxSizing: "border-box",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  filter: {
    marginLeft: theme.spacing(2),
    paddingTop: theme.spacing(3),
  },
  filterItem: { marginTop: theme.spacing(4) },
  formControl: { margin: theme.spacing(1), width: "89%" },
  buttonContainer: {
    position: "absolute",
    bottom: 45,
    left: "50%",
    transform: "translateX(-50%)",
    width: "100%",
    display: "flex",
    justifyContent: "space-around",
  },
  dataBtn: { minWidth: 200, height: theme.spacing(4), paddingTop: theme.spacing(1.5) },
  scrollArea: {
    flexGrow: 1,
    overflowY: "auto",
    paddingRight: 8,
    paddingBottom: 16,
    marginBottom: 80,
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "8px 0",
  },
  closeButton: {
    position: "absolute",
    top: 10,
    left: 8,
    zIndex: 10,
  },
  title: {
    fontSize: "1rem",
    fontWeight: "bold",
    marginLeft: 42,
  },
  checkboxItem: {
    marginLeft: theme.spacing(1),
  },
}));

export const FilterDrawerSaitama = ({
  toggleDrawer,
  anchor,
  filterStore,
  reportFilterStore,
  reportTagFilterData,
  filterType,
  setFilter,
  setCurrentPageNumber,
  getHomeImageData,
  setReportFilter,
  setReportCurrentPageNumber,
  getReportData,
}) => {
  const classes = useStyles();
  const isDeaAdmin = getIsDeaAdmin();
  const isReport = filterType === "report";
  const [managementId, setManagementId] = useState("");
  const [inspectionItems, setInspectionItems] = useState([]);
  const [firstCaptureOnly, setFirstCaptureOnly] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("APPROVED");
  const [reportTags, setReportTags] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [sort, setSort] = useState("occurred_datetime-desc");

  useEffect(() => {
    const activeFilter = isReport ? reportFilterStore : filterStore;
    setManagementId(activeFilter.managementId || "");
    setInspectionItems(activeFilter.inspectionItems || []);
    setFirstCaptureOnly(Boolean(activeFilter.firstCaptureOnly));
    setReviewStatus(activeFilter.reviewStatus || "APPROVED");
    setReportTags(activeFilter.tag || []);
    setPlayerId(activeFilter.playerId || "");
    setSort(`${isReport ? "occurred_datetime" : (activeFilter.type || "occurred_datetime")}-${activeFilter.order || "desc"}`);
  }, [filterStore, reportFilterStore, isReport]);

  const apply = (nextFilter) => {
    if (isReport) {
      setReportFilter({ ...nextFilter, filterType: "report" });
      setReportCurrentPageNumber(1);
      getReportData({ pageNumber: 1 });
    } else {
      setFilter({ ...nextFilter, filterType: "photo" });
      setCurrentPageNumber(1);
      getHomeImageData({ pageNumber: 1 });
    }
    toggleDrawer(anchor, false)();
  };

  const handleApply = () => {
    const { type, order } = parseSaitamaSort(sort);
    if (isReport) {
      apply({ ...reportFilterStore, type, order, managementId, tag: reportTags, playerId });
    } else {
      apply(buildSaitamaPhotoFilter({
        filterStore,
        type,
        order,
        managementId,
        inspectionItems,
        firstCaptureOnly,
        reviewStatus,
      }));
    }
  };

  const handleReset = () => {
    if (isReport) {
      apply({ ...reportFilterStore, type: "occurred_datetime", order: "desc", managementId: "", tag: [], playerId: "" });
    } else {
      apply(resetSaitamaPhotoFilter(filterStore));
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <IconButton className={classes.closeButton} onClick={toggleDrawer(anchor, false)} aria-label="close filter"><CloseIcon /></IconButton>
        <Typography variant="h6" className={classes.title}>絞り込み条件設定</Typography>
      </div>
      <div className={classes.scrollArea}>
        <Grid container direction="column" className={classes.filter}>
          {isReport ? (
            <>
              <Grid item className={classes.filterItem}>
                <TextField label="管理ID" variant="outlined" className={classes.formControl} value={managementId} onChange={(e) => setManagementId(e.target.value)} />
              </Grid>
              <Grid item className={classes.filterItem}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  className={classes.formControl}
                  options={reportTagFilterData || []}
                  value={(reportTagFilterData || []).filter((item) => reportTags.includes(item.value))}
                  getOptionLabel={(option) => option.value || ""}
                  getOptionSelected={(option, value) => option.value === value.value}
                  onChange={(_, values) => setReportTags(values.map((item) => item.value))}
                  renderTags={(values, getTagProps) => values.map((option, index) => <Chip {...getTagProps({ index })} key={option.value} label={option.label || option.value} />)}
                  renderInput={(params) => <TextField {...params} label="報告タグ" variant="outlined" />}
                  renderOption={(option) => (
                    <Typography variant="body1">
                      <b>{option.label || option.value}</b> : {option.tip || ""} ({option.count || 0})
                    </Typography>
                  )}
                />
              </Grid>
              <Grid item className={classes.filterItem}>
                <TextField label="Player ID" variant="outlined" className={classes.formControl} value={playerId} onChange={(e) => setPlayerId(e.target.value)} />
              </Grid>
              <Grid item className={classes.filterItem}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="saitama-report-sort-label">ソート</InputLabel>
                  <Select labelId="saitama-report-sort-label" value={sort} onChange={(e) => setSort(e.target.value)} label="ソート">
                    <MenuItem value="occurred_datetime-desc">報告日時 - 降順</MenuItem>
                    <MenuItem value="occurred_datetime-asc">報告日時 - 昇順</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          ) : (
          <>
            <Grid item className={classes.filterItem}>
                <TextField label="管理ID" variant="outlined" className={classes.formControl} value={managementId} onChange={(e) => setManagementId(e.target.value)} />
            </Grid>
            <Grid item className={classes.filterItem}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                className={classes.formControl}
                options={SAITAMA_INSPECTION_ITEMS}
                value={SAITAMA_INSPECTION_ITEMS.filter((item) => inspectionItems.includes(item.code))}
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) => option.code === value.code}
                onChange={(_, values) => setInspectionItems(values.map((item) => item.code))}
                renderOption={(option, { selected }) => (
                  <FormControlLabel control={<Checkbox checked={selected} />} label={option.label} />
                )}
                renderTags={(values, getTagProps) => values.map((option, index) => <Chip {...getTagProps({ index })} key={option.code} label={option.label} />)}
                renderInput={(params) => <TextField {...params} label="点検項目" variant="outlined" />}
              />
            </Grid>
            <Grid item className={classes.filterItem}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="saitama-review-status-label">レビュー結果</InputLabel>
                <Select
                  labelId="saitama-review-status-label"
                  id="saitama-review-status"
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  label="レビュー結果"
                >
                  <MenuItem value="APPROVED">OK</MenuItem>
                  <MenuItem value="REJECTED">NG</MenuItem>
                  {isDeaAdmin && <MenuItem value="APPLY_WAITING">レビュー待ち</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
          </>
          )}
          {!isReport && <Grid item className={classes.filterItem}>
            <FormControlLabel className={classes.checkboxItem} control={<Checkbox checked={firstCaptureOnly} onChange={(e) => setFirstCaptureOnly(e.target.checked)} />} label="初回撮影のみ表示" />
          </Grid>}
          {!isReport && <Grid item className={classes.filterItem}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="saitama-sort-label">ソート</InputLabel>
              <Select labelId="saitama-sort-label" value={sort} onChange={(e) => setSort(e.target.value)} label="ソート">
                <MenuItem value="occurred_datetime-desc">撮影日時 - 降順</MenuItem>
                <MenuItem value="occurred_datetime-asc">撮影日時 - 昇順</MenuItem>
              </Select>
            </FormControl>
          </Grid>}
        </Grid>
      </div>
      <div className={classes.buttonContainer}>
        <Button className={classes.dataBtn} variant="contained" color="primary" onClick={handleApply}>絞り込む</Button>
        <Button className={classes.dataBtn} variant="contained" onClick={handleReset}>リセット</Button>
      </div>
    </div>
  );
};

FilterDrawerSaitama.propTypes = { toggleDrawer: PropTypes.func.isRequired, anchor: PropTypes.string.isRequired };

export default connect(
  (state) => ({
    filterStore: state.home.filter,
    reportFilterStore: state.report.filter,
    reportTagFilterData: state.report.reportTagFilterData,
  }),
  (dispatch) => ({
    setFilter: (value) => dispatch(setFilter(value)),
    setCurrentPageNumber: (value) => dispatch(setCurrentPageNumber(value)),
    getHomeImageData: (value) => dispatch(getHomeImageData(value)),
    setReportFilter: (value) => dispatch(setReportFilter(value)),
    setReportCurrentPageNumber: (value) => dispatch(setReportCurrentPageNumber(value)),
    getReportData: (value) => dispatch(getReportData(value)),
  }),
)(FilterDrawerSaitama);
